import i18n from './i18n';

// Route mappings for localized URLs
const routeMappings = {
  sk: {
    '/': '/',
    '/dashboard': '/nastenka',
    '/clients': '/klienti',
    '/tasks': '/ulohy',
    '/invoices': '/faktury',
    '/price-quotes': '/cenove-ponuky',
    '/dobropisy': '/dobropisy',
    '/reminders': '/pripomienky',
    '/team': '/tym',
    '/profile': '/profil',
    '/subscribe': '/predplatne',
    '/admin': '/admin',
    '/admin/subscription-plans': '/admin/plany-predplatneho',
    '/admin/update-center': '/admin/centrum-aktualizacii',
    '/admin/enterprise': '/admin/enterprise',
    '/upgrade': '/upgrade',
    '/pricing': '/cenik',
    '/weekly-planner': '/tyzdenny-planovac',
    '/personal-tasks': '/osobne-ulohy',
  },
  en: {
    '/': '/',
    '/dashboard': '/dashboard',
    '/clients': '/clients',
    '/tasks': '/tasks',
    '/invoices': '/invoices',
    '/price-quotes': '/price-quotes',
    '/dobropisy': '/credit-notes',
    '/reminders': '/reminders',
    '/team': '/team',
    '/profile': '/profile',
    '/subscribe': '/subscribe',
    '/admin': '/admin',
    '/admin/subscription-plans': '/admin/subscription-plans',
    '/admin/update-center': '/admin/update-center',
    '/admin/enterprise': '/admin/enterprise',
    '/upgrade': '/upgrade',
    '/pricing': '/pricing',
    '/weekly-planner': '/weekly-planner',
    '/personal-tasks': '/personal-tasks',
  }
};

// Reverse mappings for URL parsing
const reverseMappings = {
  sk: {
    '/': '/',
    '/nastenka': '/dashboard',
    '/klienti': '/clients',
    '/ulohy': '/tasks',
    '/faktury': '/invoices',
    '/cenove-ponuky': '/price-quotes',
    '/dobropisy': '/dobropisy',
    '/pripomienky': '/reminders',
    '/tym': '/team',
    '/profil': '/profile',
    '/predplatne': '/subscribe',
    '/admin': '/admin',
    '/admin/plany-predplatneho': '/admin/subscription-plans',
    '/admin/centrum-aktualizacii': '/admin/update-center',
    '/admin/enterprise': '/admin/enterprise',
    '/upgrade': '/upgrade',
    '/cenik': '/pricing',
    '/tyzdenny-planovac': '/weekly-planner',
    '/osobne-ulohy': '/personal-tasks',
  },
  en: {
    '/': '/',
    '/dashboard': '/dashboard',
    '/clients': '/clients',
    '/tasks': '/tasks',
    '/invoices': '/invoices',
    '/price-quotes': '/price-quotes',
    '/credit-notes': '/dobropisy',
    '/reminders': '/reminders',
    '/team': '/team',
    '/profile': '/profile',
    '/subscribe': '/subscribe',
    '/admin': '/admin',
    '/admin/subscription-plans': '/admin/subscription-plans',
    '/admin/update-center': '/admin/update-center',
    '/admin/enterprise': '/admin/enterprise',
    '/upgrade': '/upgrade',
    '/pricing': '/pricing',
    '/weekly-planner': '/weekly-planner',
    '/personal-tasks': '/personal-tasks',
  }
};

/**
 * Get the localized URL for invoice detail (e.g. /faktury/123 or /en/invoices/123)
 */
export function getLocalizedInvoiceDetailUrl(invoiceId: number | string, language?: string): string {
  const base = getLocalizedUrl('/invoices', language);
  return `${base}/${invoiceId}`;
}

/**
 * Get the localized URL for a given route
 */
export function getLocalizedUrl(route: string, language?: string): string {
  const lang = language || i18n.language || 'sk';
  const mapping = routeMappings[lang as keyof typeof routeMappings];
  
  if (!mapping || !mapping[route as keyof typeof mapping]) {
    // Fallback to original route with language prefix
    return lang === 'sk' ? route : `/${lang}${route}`;
  }
  
  const localizedPath = mapping[route as keyof typeof mapping];
  return lang === 'sk' ? localizedPath : `/${lang}${localizedPath}`;
}

/**
 * Parse a localized URL to get the original route and language
 */
export function parseLocalizedUrl(url: string): { route: string; language: string } {
  const pathSegments = url.split('/').filter(Boolean);
  
  // Check if first segment is a language code
  if (pathSegments.length > 0 && ['sk', 'en'].includes(pathSegments[0])) {
    const language = pathSegments[0];
    const pathWithoutLang = '/' + pathSegments.slice(1).join('/');
    
    // Try to find the original route using reverse mapping
    const reverseMapping = reverseMappings[language as keyof typeof reverseMappings];
    if (reverseMapping && reverseMapping[pathWithoutLang as keyof typeof reverseMapping]) {
      return {
        route: reverseMapping[pathWithoutLang as keyof typeof reverseMapping],
        language
      };
    }
    
    // Fallback: return the path without language prefix
    return { route: pathWithoutLang, language };
  }
  
  // No language prefix, assume Slovak
  return { route: url, language: 'sk' };
}

/**
 * Normalize a path by removing query strings and hash fragments
 */
function normalizePath(path: string): string {
  // Remove query string and hash
  return path.split('?')[0].split('#')[0];
}

/**
 * Check if a URL is active (matches current location)
 */
export function isActiveUrl(currentPath: string, targetRoute: string, language?: string): boolean {
  const lang = language || i18n.language || 'sk';
  const localizedTarget = getLocalizedUrl(targetRoute, lang);
  
  // Normalize paths to remove query strings and hash fragments
  const normalizedCurrentPath = normalizePath(currentPath);
  const normalizedTarget = normalizePath(localizedTarget);
  
  // Handle root path specially - must match exactly
  if (targetRoute === '/') {
    // Check exact matches for root path
    if (normalizedCurrentPath === '/' || normalizedCurrentPath === `/${lang}`) {
      return true;
    }
    // Check localized dashboard paths (both languages)
    const dashboardPaths = [
      '/dashboard', 
      '/app/dashboard', 
      '/nastenka', 
      '/sk/nastenka', 
      '/en/dashboard',
      getLocalizedUrl('/dashboard', 'sk'),
      getLocalizedUrl('/dashboard', 'en'),
      getLocalizedUrl('/', 'sk'),
      getLocalizedUrl('/', 'en')
    ];
    // Remove duplicates and check
    const uniqueDashboardPaths = [...new Set(dashboardPaths)];
    if (uniqueDashboardPaths.includes(normalizedCurrentPath)) {
      return true;
    }
    // Root path should not match other paths
    return false;
  }
  
  // For other routes, check if current path starts with the localized target
  // But ensure it's not just a partial match (e.g., /tasks shouldn't match /task)
  if (normalizedCurrentPath === normalizedTarget) {
    return true;
  }
  
  // Check if current path is a subpath of the target (e.g., /tasks/123 matches /tasks)
  // But exclude root path matches
  if (normalizedCurrentPath.startsWith(normalizedTarget + '/')) {
    return true;
  }
  
  return false;
}

/**
 * Get all possible URL variations for a route (for redirects)
 */
export function getAllUrlVariations(route: string): string[] {
  const variations: string[] = [];
  
  // Add both language variations
  variations.push(getLocalizedUrl(route, 'sk'));
  variations.push(getLocalizedUrl(route, 'en'));
  
  // Add original route for backward compatibility
  if (route !== '/') {
    variations.push(route);
  }
  
  return variations;
}
